import * as React from 'react'

import { MapRef } from 'react-map-gl/mapbox'

// The collision grid's own viewportPadding: a pin this far off screen is still placed or culled,
// so a caption there can be judged; past it nothing is placed and nothing can be.
const VIEWPORT_PADDING_PX = 100

export interface ICaptionAnchor {
  // The coordKey its pin carries.
  key: string
  longitude: number
  latitude: number
}

type ScreenPoint = { x: number; y: number }

// Pure half of the probe: the captions inside the padded viewport whose pin did not place.
export function culledCaptionKeys(
  anchors: ICaptionAnchor[],
  placedPinKeys: Set<string>,
  project: (anchor: ICaptionAnchor) => ScreenPoint,
  viewport: { width: number; height: number },
): Set<string> {
  const culled = new Set<string>()
  anchors.forEach(anchor => {
    if (placedPinKeys.has(anchor.key)) return
    const { x, y } = project(anchor)
    if (
      x >= -VIEWPORT_PADDING_PX &&
      y >= -VIEWPORT_PADDING_PX &&
      x <= viewport.width + VIEWPORT_PADDING_PX &&
      y <= viewport.height + VIEWPORT_PADDING_PX
    ) {
      culled.add(anchor.key)
    }
  })
  return culled
}

const isSameSet = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every(key => b.has(key))

const EMPTY = new Set<string>()

// A caption is its own symbol, so mapbox places it even when its pin lost: an orphan. Nothing in
// GL ties the two across layers, so once placement settles this asks which pins it placed and
// names the captions to drop (STX1-1143). Read-only: qRF reads the placement, it changes nothing.
export function useCulledPinKeys(
  map: MapRef | undefined,
  pinLayerIds: string[] | undefined,
  anchors: ICaptionAnchor[],
): Set<string> {
  const [culled, setCulled] = React.useState<Set<string>>(EMPTY)
  const anchorsRef = React.useRef(anchors)
  anchorsRef.current = anchors
  const enabled = !!pinLayerIds?.length && anchors.length > 0
  const layersKey = pinLayerIds?.join('|')

  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl || !enabled) {
      setCulled(EMPTY)
      return undefined
    }

    const probe = (): void => {
      const present = pinLayerIds.filter(id => gl.getLayer(id))
      if (!present.length) return
      const canvas = gl.getCanvas()
      const width = canvas.clientWidth
      const height = canvas.clientHeight
      const placed = new Set(
        gl
          .queryRenderedFeatures(
            [
              [-VIEWPORT_PADDING_PX, -VIEWPORT_PADDING_PX],
              [width + VIEWPORT_PADDING_PX, height + VIEWPORT_PADDING_PX],
            ],
            { layers: present },
          )
          .map(feature => feature.properties?.coordKey as string),
      )
      const next = culledCaptionKeys(
        anchorsRef.current,
        placed,
        anchor => gl.project([anchor.longitude, anchor.latitude]),
        { width, height },
      )
      setCulled(previous => (isSameSet(previous, next) ? previous : next))
    }

    gl.on('idle', probe)
    gl.triggerRepaint()
    return () => {
      gl.off('idle', probe)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabled, layersKey])

  return culled
}
